import { useCallback, useEffect, useRef } from 'react';

import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import { MediaElement } from 'editor/src/store/design/types';
import getSelectedElementUuids from 'editor/src/store/editor/selector/getSelectedElementUuids';
import { useStore } from 'editor/src/store/hooks';

import CustomFabricActiveSelection from 'editor/src/fabric/CustomFabricActiveSelection';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';

import { getActiveSelection } from 'editor/src/component/EditorArea/utils/groupSelectionUtils';

type DimensionProps = {
  // Common properties for rectangles and images
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  r?: number;
  // Line specific properties
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
};

// Optimize by comparing primitives directly instead of creating objects
const hasDimensionsChanged = (prev: DimensionProps, curr: MediaElement): boolean => {
  if (curr.type === 'line') {
    return prev.x1 !== curr.x1 || prev.y1 !== curr.y1 || prev.x2 !== curr.x2 || prev.y2 !== curr.y2;
  }

  return (
    prev.x !== curr.x ||
    prev.y !== curr.y ||
    prev.width !== curr.width ||
    prev.height !== curr.height ||
    prev.r !== curr.r
  );
};

/**
 * Hook that tracks dimension-affecting properties of selected elements and triggers a re-render when they change
 */
export function useDimensionChangeRender() {
  const fabricCanvas = useFabricCanvas();
  const store = useStore();
  const lastValues = useRef(new Map<number, DimensionProps>());
  const lastSelectedUuids = useRef<number[]>([]);
  const rafId = useRef<number>();

  // Memoize the check function
  const checkForChanges = useCallback(() => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    rafId.current = requestAnimationFrame(() => {
      const state = store.getState();
      const selectedUuids = getSelectedElementUuids(state);

      // Quick exit if selection count is 0-1
      if (selectedUuids.length <= 1) {
        if (lastValues.current.size > 0) {
          lastValues.current.clear();
          lastSelectedUuids.current = [];
        }
        return;
      }

      // Check if selection has changed
      const hasSelectionChanged =
        selectedUuids.length !== lastSelectedUuids.current.length ||
        selectedUuids.some((uuid, i) => uuid !== lastSelectedUuids.current[i]);

      if (hasSelectionChanged) {
        lastValues.current.clear();
        lastSelectedUuids.current = selectedUuids;
      }

      // Get selected elements only if we need them
      const selectedElements = getSelectedMediaElementsObjects(state);
      if (!selectedElements?.length) {
        return;
      }

      // Initialize values if needed
      if (lastValues.current.size === 0) {
        selectedElements.forEach((element) => {
          const props =
            element.type === 'line'
              ? { x1: element.x1, y1: element.y1, x2: element.x2, y2: element.y2 }
              : { x: element.x, y: element.y, width: element.width, height: element.height, r: element.r };
          lastValues.current.set(element.uuid, props);
        });
        return;
      }

      let hasChanges = false;

      // Check for changes using optimized comparison
      selectedElements.forEach((element) => {
        const lastValue = lastValues.current.get(element.uuid);
        if (!lastValue) {
          return;
        }

        if (hasDimensionsChanged(lastValue, element)) {
          hasChanges = true;
          const newProps =
            element.type === 'line'
              ? { x1: element.x1, y1: element.y1, x2: element.x2, y2: element.y2 }
              : { x: element.x, y: element.y, width: element.width, height: element.height, r: element.r };
          lastValues.current.set(element.uuid, newProps);
        }
      });

      if (hasChanges) {
        const activeSelection = getActiveSelection(fabricCanvas);
        if (activeSelection) {
          const activeObjects = [...activeSelection.getObjects()];
          const newActiveSelection = new CustomFabricActiveSelection(activeObjects, { canvas: fabricCanvas });
          fabricCanvas.setActiveObject(newActiveSelection);
          fabricCanvas.requestRenderAll();
        }
      }
    });
  }, [store, fabricCanvas]);

  useEffect(() => {
    const unsubscribe = store.subscribe(checkForChanges);
    return () => {
      unsubscribe();
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [checkForChanges]);
}
